/**
 * Override du mode de récompense pour un contexte (réutilisabilité — Étape 9B).
 * Réutilise RewardsModeSelector avec l'option « Hériter » : un organisateur peut
 * activer/forcer les cadeaux (ou le virtuel) sur UN challenge ou UN parcours, sans
 * toucher au réglage général de l'entreprise. Enregistrement immédiat.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { bizCoinsApi } from "../lib/api";
import { RewardsModeSelector, type RewardsModeValue } from "./RewardsModeSelector";

export function ScopeRewardsModeControl({
  scope,
  id,
  initialMode,
  tenantMode,
}: {
  scope: "challenge" | "path";
  id: string;
  initialMode: "virtual" | "catalog" | null; // null = hérite
  tenantMode: "virtual" | "catalog";
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState<RewardsModeValue>(initialMode ?? "inherit");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);

  /**
   * L'ancienne version posait `setValue(v)` AVANT l'appel et avalait l'erreur :
   * l'écran affichait le nouveau mode de récompense alors que rien n'était
   * enregistré. On revient donc à la valeur précédente en cas d'échec, et on
   * le dit — un réglage de récompense qu'on croit actif sans qu'il le soit
   * fausse tout un challenge.
   */
  const change = async (v: RewardsModeValue) => {
    const previous = value;
    setValue(v);
    setSaved(false);
    setError(false);
    const mode = v === "inherit" ? "inherit" : v;
    try {
      if (scope === "challenge") await bizCoinsApi.setChallengeRewardsMode(id, mode);
      else await bizCoinsApi.setPathRewardsMode(id, mode);
      setSaved(true);
    } catch (err) {
      console.error("[rewards-mode] enregistrement impossible:", err);
      setValue(previous);
      setError(true);
    }
  };

  return (
    <div className="rounded-xl border p-4" style={{ borderColor: "#E4E7EF" }}>
      <h4 className="font-semibold text-sm mb-1" style={{ color: "#384872" }}>{t("bizRewards.scopeTitle")}</h4>
      <p className="text-xs text-gray-500 mb-3">{t("bizRewards.scopeSubtitle")}</p>
      <RewardsModeSelector value={value} onChange={change} allowInherit inheritedMode={tenantMode} />
      {saved && <p className="mt-2 text-xs font-medium" style={{ color: "#1E7A3C" }}>{t("common.saved")}</p>}
      {error && (
        <p role="alert" className="mt-2 text-xs font-medium" style={{ color: "#C0423F" }}>
          {t("bizRewards.scopeSaveError")}
        </p>
      )}
    </div>
  );
}
